/**
 * KOA-17818 — the impact report behind the designer's "Where Used" view for an action.
 *
 * Mirrors `Backend/CoreApi/Services/ActionUsageModels.cs`. Screens are scoped to the current
 * project (the same scope the delete guard enforces); scheduled jobs, workflows and API Builder
 * endpoints are domain-wide.
 */

/**
 * How the action is reached from the place that uses it. The distinction is what makes the report
 * actionable: `Event` means the element RUNS the action, `DataBinding` means it DISPLAYS the
 * result — changing the return shape breaks the second but not necessarily the first.
 *
 * Numeric values must stay in lockstep with the C# `EActionUsageKind`, which serializes as an int.
 */
export enum EActionUsageKind {
  Event = 0,
  DataBinding = 1,
  Parameter = 2,
  Condition = 3,
  Unknown = 4
}

/** One place inside a single screen where the action is used. */
export interface IActionUsageLocationDto {
  /** Display-item id (kuikaId). Null when the usage is screen-level. */
  elementId: number | null;
  /** The element's friendly name, falling back to its component type. */
  elementName: string;
  /** Component type, e.g. Button / Table. Empty for screen-level usages. */
  elementType: string;
  kind: EActionUsageKind;
  /** Event name for `Event`, property name otherwise. */
  detail: string;
}

/** Every usage found in one screen. */
export interface IActionUsageScreenDto {
  screenId: string;
  screenName: string;
  locations: IActionUsageLocationDto[];
  /**
   * The screen provably references the action but the walk could not attribute it to an element
   * (legacy JSON). The screen is still listed — under-reporting impact is the one failure mode
   * this report must not have.
   */
  isPartial: boolean;
}

/** A non-screen consumer: a scheduled job, a workflow or an API Builder endpoint. */
export interface IActionUsageEntityDto {
  id: string;
  name: string;
  projectId: string;
  projectName: string;
}

export interface IActionUsageReportDto {
  actionId: string;
  actionName: string;
  screens: IActionUsageScreenDto[];
  scheduledJobs: IActionUsageEntityDto[];
  workflows: IActionUsageEntityDto[];
  apiEndpoints: IActionUsageEntityDto[];
  /** Screens + jobs + workflows + endpoints. Zero means the action is safe to change. */
  totalCount: number;
}
